import { MaterialCommunityIcons } from "@expo/vector-icons";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { ActivityIndicator, Button } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import {
    AssessmentScreenHeader,
    assessmentScreenStyles
} from "@/components/assessment/AssessmentScreenHeader";
import { SubmitButton } from "@/components/assessment/SubmitButton";
import { TextLongQuestion } from "@/components/questions/TextQuestion";
import { Chapter } from "@/components/section/Chapter";
import { SpecRow } from "@/components/section/SpecRow";
import { fetchVisitForm } from "@/lib/api";
import {
    fetchCorrectionRequests,
    submitCorrectionAnswers,
    type RnCorrectionRequest
} from "@/lib/correction-requests";
import { useFormContext } from "@/lib/FormContext";
import { colors, fonts, radius } from "@/lib/theme";

function QuestionBatch({
    item,
    refresh
}: {
    item: RnCorrectionRequest;
    refresh: () => Promise<void>;
}) {
    const { updateForm } = useFormContext();
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [busyQuestions, setBusyQuestions] = useState<Record<number, boolean>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const complete = item.questions.every((q) => answers[q.questionId]?.trim());
    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            await submitCorrectionAnswers(
                item.id,
                item.questions.map((q) => ({
                    questionId: q.questionId,
                    text: answers[q.questionId]
                }))
            );
            const updated = await fetchVisitForm(String(item.visitId));
            updateForm(() => updated);
            await refresh();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not submit. Please try again.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <View>
            {item.questions.map((q) => (
                <View key={q.questionId} style={{ marginBottom: 24 }}>
                    <SpecRow label={q.text} required>
                        <TextLongQuestion
                            visitId={String(item.visitId)}
                            sectionId={String(q.sectionId)}
                            recorderMode="section"
                            saveLabel={item.answeredAt ? "submitted" : "not submitted"}
                            question={{
                                id: String(q.questionId),
                                text: q.text,
                                type: "text_long",
                                orderIndex: 0,
                                mandatory: true,
                                visibleOnMobile: true,
                                defaultValue: null,
                                config: null,
                                parentQuestionId: null,
                                patientDataKey: null,
                                hintText: "Tap to type…",
                                answerOptions: [],
                                branchingRules: []
                            }}
                            value={
                                item.answers?.find((a) => a.questionId === q.questionId)?.text ??
                                answers[q.questionId] ??
                                ""
                            }
                            onChange={(text) =>
                                setAnswers((prev) => ({ ...prev, [q.questionId]: text }))
                            }
                            readOnly={!!item.answeredAt || saving}
                            onRecordingBusyChange={(busy) =>
                                setBusyQuestions((prev) =>
                                    prev[q.questionId] === busy
                                        ? prev
                                        : { ...prev, [q.questionId]: busy }
                                )
                            }
                        />
                    </SpecRow>
                </View>
            ))}
            {item.answeredAt ? (
                <Text style={styles.success}>
                    Answers submitted. Thank you—your clarification has been sent for review.
                </Text>
            ) : (
                <SubmitButton
                    inline
                    allComplete={!!complete && !Object.values(busyQuestions).some(Boolean)}
                    disabled={saving || !complete || Object.values(busyQuestions).some(Boolean)}
                    loading={saving}
                    label={saving ? "SUBMITTING…" : "SUBMIT ANSWERS"}
                    onPress={submit}
                />
            )}
            {!!error && (
                <Text accessibilityRole="alert" style={styles.error}>
                    {error}
                </Text>
            )}
        </View>
    );
}

export default function ClarificationsScreen() {
    const { id } = useLocalSearchParams<{ id: string }>();
    const { form } = useFormContext();
    const [items, setItems] = useState<RnCorrectionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const refresh = useCallback(async () => {
        try {
            setItems((await fetchCorrectionRequests()).filter((r) => String(r.visitId) === id));
            setError("");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not load questions");
        } finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => {
        void refresh();
    }, [refresh]);
    return (
        <SafeAreaView style={assessmentScreenStyles.container} edges={["top"]}>
            <AssessmentScreenHeader
                title="Clarifications"
                onBack={() => (router.canGoBack() ? router.back() : router.replace(`/visit/${id}`))}
            />
            <ScrollView
                style={assessmentScreenStyles.scroll}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag"
                contentContainerStyle={assessmentScreenStyles.content}
            >
                <Chapter
                    num="01"
                    title="Clarifications"
                    helper="Answer from what you observed or learned during this visit."
                >
                    <View style={styles.patientRow}>
                        <View style={styles.patientIcon}>
                            <MaterialCommunityIcons
                                name="account-outline"
                                size={22}
                                color={colors.teal700}
                            />
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.patientName}>
                                {form.patient?.firstName} {form.patient?.lastName}
                            </Text>
                            <Text style={styles.hint}>Visit #{id}</Text>
                        </View>
                    </View>
                    <Text style={styles.instructions}>
                        Please clarify the questions below based on your visit. If you didn’t assess
                        something or don’t remember, please say so.
                    </Text>

                    {loading && <ActivityIndicator />}
                    {!!error && (
                        <View>
                            <Text accessibilityRole="alert">{error}</Text>
                            <Button onPress={refresh}>Retry</Button>
                        </View>
                    )}
                    {!loading && !error && !items.length && (
                        <Text>No clarification requests for this visit.</Text>
                    )}
                    {items.map((item) => (
                        <QuestionBatch key={item.id} item={item} refresh={refresh} />
                    ))}
                </Chapter>
            </ScrollView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    patientRow: { flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 20 },
    patientIcon: {
        width: 44,
        height: 44,
        borderRadius: radius.md,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    patientName: {
        fontFamily: fonts.bold,
        fontSize: 16,
        color: colors.textPrimary,
        marginBottom: 4
    },
    hint: {
        fontFamily: fonts.regular,
        fontSize: 12,
        lineHeight: 18,
        color: colors.textSecondary,
        flexShrink: 1
    },
    instructions: {
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 22,
        color: colors.textSecondary,
        marginBottom: 28
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 11,
        letterSpacing: 1,
        color: colors.textSecondary,
        marginBottom: 12
    },
    error: {
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 21,
        color: colors.error600,
        paddingVertical: 12
    },
    success: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        lineHeight: 22,
        color: colors.teal700,
        backgroundColor: colors.teal50,
        borderRadius: radius.md,
        padding: 16,
        marginBottom: 16
    }
});
